<template>
    <div class="flex flex-col items-center gap-10 w-full max-w-3xl">
        <div v-if="greetingFlow && greetingFlow.length > 0">
            <div class="flex justify-center gap-2 mb-10">
                <button
                    v-for="(page, index) in greetingFlow"
                    :key="index"
                    :aria-label="t('onboarding.greeting.goToPage', { page: index + 1 })"
                    :class="[
                        'transition-[width,background-color] duration-(--motion-duration-normal) motion-reduce:transition-[background-color]',
                        currentPage === index ? 'w-8 h-2 bg-control-accent rounded-full' : 'w-2 h-2 bg-border-strong rounded-full hover:bg-control-border-strong',
                    ]"
                    @click="currentPage = index" />
            </div>

            <Transition name="slide-fade" mode="out-in">
                <div :key="currentPage" class="text-center w-full mb-10 min-h-[300px] md:min-h-[250px] flex flex-col justify-start">
                    <div v-if="currentPage === 0" class="mb-10">
                        <span class="text-ink display-medium">{{ t("onboarding.greeting.intro", { name: firstName }) }}</span>
                    </div>

                    <h2 class="display-medium text-balance mb-6">
                        {{ greetingFlow[currentPage]?.title }}
                    </h2>

                    <p class="text-ink-muted title-body max-w-2xl mx-auto text-pretty">
                        {{ greetingFlow[currentPage]?.subtitle }}
                    </p>
                </div>
            </Transition>

            <div class="flex justify-center items-center w-full gap-4">
                <BaseButton v-if="currentPage > 0" theme="tertiary" @click="currentPage--">{{ t("onboarding.greeting.backButton") }}</BaseButton>

                <BaseButton v-if="currentPage < greetingFlow.length - 1" theme="primary" data-testid="greeting-next" @click="currentPage++">{{
                    t("onboarding.greeting.nextButton")
                }}</BaseButton>
                <BaseButton v-else theme="primary" data-testid="get-started" @click="handleGetStarted">{{ t("onboarding.greeting.getStartedButton") }}</BaseButton>
            </div>
        </div>

        <div v-else class="text-center leading-tight">
            <div class="max-w-xl display-medium mb-10">
                <h2 class="mb-3">
                    <span class="text-ink">{{ t("onboarding.greeting.intro", { name: firstName }) }}</span>
                    <br />{{ t("onboarding.greeting.tagline") }}
                </h2>
                <p class="text-ink-muted text-center display-serif">
                    {{
                        organizationName
                            ? t("onboarding.greeting.roleIntroWithOrganization", { role, article: articleFor(role), organizationName })
                            : t("onboarding.greeting.roleIntro", { role, article: articleFor(role) })
                    }}
                </p>
            </div>
            <BaseButton theme="primary" class="w-[300px] leading-6 body-small" data-testid="get-started" @keydown.enter="handleGetStarted" @click="handleGetStarted">
                {{ t("onboarding.greeting.getStartedButton") }}
            </BaseButton>
        </div>
    </div>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import { articleFor } from "~vue/utils/stringUtils";
import { ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

interface GreetingPage {
    title: string;
    subtitle: string;
}

interface Props {
    greetingFlow?: GreetingPage[];
    firstName: string;
    role?: string;
    organizationName?: string;
}

withDefaults(defineProps<Props>(), {
    greetingFlow: () => [],
    role: "",
    organizationName: "",
});

const emit = defineEmits<{
    getStarted: [];
}>();

const currentPage = ref<number>(0);

function handleGetStarted(): void {
    emit("getStarted");
}
</script>

<style scoped>
/* Slide transition for multi-page greeting carousel */
.slide-fade-enter-active,
.slide-fade-leave-active {
    transition:
        opacity var(--motion-duration-slow) var(--motion-ease-standard),
        transform var(--motion-duration-slow) var(--motion-ease-standard);
}

.slide-fade-enter-from {
    opacity: 0;
    transform: translateX(30px);
}

.slide-fade-leave-to {
    opacity: 0;
    transform: translateX(-30px);
}

@media (prefers-reduced-motion: reduce) {
    .slide-fade-enter-active,
    .slide-fade-leave-active {
        transition-property: opacity;
    }

    .slide-fade-enter-from,
    .slide-fade-leave-to {
        transform: none;
    }
}
</style>
